import React, { useState, useRef, useEffect } from 'react';
import './Dropdown.scss';

interface DropdownOption {
  value: string;
  label: string;
  title?: string;
}

interface DropdownProps {
  id: string;
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  width?: string;
}

const Dropdown: React.FC<DropdownProps> = ({
  id,
  value,
  options,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  className = '',
  width = 'auto'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLSelectElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(option => option.value === value);
  const displayValue = selectedOption ? selectedOption.label : placeholder;

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={`dropdown ${className}`} style={{ width, maxWidth: '100%' }} ref={dropdownRef}>
      <button
        type="button"
        className={`dropdown__trigger ${isOpen ? 'dropdown__trigger--open' : ''} ${disabled ? 'dropdown__trigger--disabled' : ''}`}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Select ${placeholder}`}
        title={displayValue}
        id={`${id}-trigger`}
      >
        <span className="dropdown__value">{displayValue}</span>
        <svg
          className={`dropdown__chevron ${isOpen ? 'dropdown__chevron--open' : ''}`}
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M7 10L12 15L17 10" stroke="#606060" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      
      {isOpen && (
        <div className="dropdown__menu-container">
          {/* Native select for accessibility */}
          <select
            ref={selectRef}
            className="dropdown__select-element visually-hidden"
            aria-label={`${placeholder} options`}
            value={value}
            onChange={(e) => handleSelect(e.target.value)}
            size={options.length > 5 ? 5 : options.length}
            id={id}
          >
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                title={option.title ?? option.label}
              >
                {option.title ?? option.label}
              </option>
            ))}
          </select>

          {/* Custom UI for styling and interaction control */}
          <ul
            className="dropdown__menu"
            aria-hidden="true"
          >
            {options.map((option) => (
              <li key={option.value}>
                <button
                  type="button"
                  className={`dropdown__option ${value === option.value ? 'dropdown__option--selected' : ''}`}
                  onClick={() => handleSelect(option.value)}
                  data-selected={value === option.value}
                  title={option.title ?? option.label}
                  tabIndex={-1}
                >
                  {option.title ?? option.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
